import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Copy, UserPlus } from "lucide-react";

const GRADES = ["PreK", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Quran", "Arabic"];
const errMsg = (e) => {
    const d = e.response?.data?.detail;
    return Array.isArray(d) ? d.map(x => x.msg).join(", ") : d || "Something went wrong";
};

function TempPassword({ result }) {
    function copy() {
        navigator.clipboard.writeText(result.temp_password);
        toast.success("Temporary password copied");
    }
    return (
        <div className="space-y-3" data-testid="add-parent-result">
            <p className="text-sm text-brand-stone">
                <b>{result.full_name}</b> ({result.email}) was added and a welcome email was sent (it doesn't include the password). Share this temporary password with them — they'll be asked to change it on first login. They can also use "Forgot password".
            </p>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 flex items-center justify-between gap-2 font-mono">
                <span className="select-all" data-testid="add-parent-temp-password">{result.temp_password}</span>
                <Button variant="outline" size="sm" onClick={copy} className="gap-1.5" data-testid="add-parent-copy"><Copy className="w-3.5 h-3.5" /> Copy</Button>
            </div>
        </div>
    );
}

export function AddParentDialog({ onDone }) {
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({ full_name: "", email: "", phone: "" });
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    async function submit() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/parents", { ...form, phone: form.phone || undefined });
            setResult(data);
            onDone?.();
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    }
    function close(v) {
        setOpen(v);
        if (!v) { setResult(null); setForm({ full_name: "", email: "", phone: "" }); }
    }
    return (
        <>
            <Button onClick={() => setOpen(true)} className="gap-1.5 bg-primary hover:bg-primary/90" data-testid="add-parent-btn"><UserPlus className="w-4 h-4" /> Add parent</Button>
            <Dialog open={open} onOpenChange={close}>
                <DialogContent data-testid="add-parent-dialog">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Add parent</DialogTitle></DialogHeader>
                    {result ? <TempPassword result={result} /> : (
                        <div className="space-y-3">
                            <div><Label>Full name</Label><Input value={form.full_name} onChange={set("full_name")} data-testid="add-parent-name" /></div>
                            <div><Label>Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="add-parent-email" /></div>
                            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={set("phone")} data-testid="add-parent-phone" /></div>
                            <Button onClick={submit} disabled={busy || !form.full_name || !form.email} className="w-full" data-testid="add-parent-submit">{busy ? "Adding…" : "Add parent"}</Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

const EMPTY_STUDENT = { parent_id: "", first_name: "", last_name: "", date_of_birth: "", grade: "", gender: "" };

function PickSelect({ value, onChange, placeholder, testid, children }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent className="max-h-72">{children}</SelectContent>
        </Select>
    );
}

export function AddStudentDialog({ onDone }) {
    const [open, setOpen] = React.useState(false);
    const [parents, setParents] = React.useState([]);
    const [form, setForm] = React.useState(EMPTY_STUDENT);
    const [busy, setBusy] = React.useState(false);
    const set = (k) => (v) => setForm(f => ({ ...f, [k]: v?.target ? v.target.value : v }));

    React.useEffect(() => {
        if (open) { api.get("/admin/parents").then(r => setParents(r.data)); }
    }, [open]);

    async function submit() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/students", form);
            toast.success(`${data.first_name} added${data.assigned_class ? ` to ${data.assigned_class.class_name}` : ""}`);
            setOpen(false);
            setForm(EMPTY_STUDENT);
            onDone?.();
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    }
    const ready = Object.values(form).every(Boolean);
    return (
        <>
            <Button variant="outline" onClick={() => setOpen(true)} className="gap-1.5" data-testid="add-student-btn"><UserPlus className="w-4 h-4" /> Add student</Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="add-student-dialog">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Add student</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Parent</Label>
                            <PickSelect value={form.parent_id} onChange={set("parent_id")} placeholder="Choose a parent" testid="add-student-parent">
                                {parents.map(p => <SelectItem key={p.id} value={p.id}>{p.full_name} · {p.email}</SelectItem>)}
                            </PickSelect>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>First name</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="add-student-first" /></div>
                            <div><Label>Last name</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="add-student-last" /></div>
                        </div>
                        <div><Label>Date of birth</Label><Input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} data-testid="add-student-dob" /></div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Grade</Label>
                                <PickSelect value={form.grade} onChange={set("grade")} placeholder="Grade" testid="add-student-grade">
                                    {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                </PickSelect>
                            </div>
                            <div>
                                <Label>Gender</Label>
                                <PickSelect value={form.gender} onChange={set("gender")} placeholder="Gender" testid="add-student-gender">
                                    <SelectItem value="male">Boy</SelectItem>
                                    <SelectItem value="female">Girl</SelectItem>
                                </PickSelect>
                            </div>
                        </div>
                        <p className="text-xs text-brand-stone">The student is placed in a class automatically. No invoice is created.</p>
                        <Button onClick={submit} disabled={busy || !ready} className="w-full" data-testid="add-student-submit">{busy ? "Adding…" : "Add student"}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function ClassAssignSelect({ student, classes, onDone }) {
    async function assign(class_id) {
        if (class_id === student.class_id) return;
        const cls = classes.find(c => c.id === class_id);
        if (cls?.grade !== student.grade &&
            !window.confirm(`Move ${student.first_name} to ${cls?.name}? Their grade will change to ${cls?.grade}.`)) return;
        try {
            const { data } = await api.post(`/admin/students/${student.id}/assign`, { class_id });
            toast.success(`${student.first_name} assigned to ${data.class_name}`);
            onDone?.();
        } catch (e) { toast.error(errMsg(e)); }
    }
    return (
        <Select value={student.class_id || ""} onValueChange={assign}>
            <SelectTrigger className={`h-8 w-44 text-xs ${student.class_id ? "" : "border-amber-400 text-amber-700"}`} data-testid={`assign-class-${student.id}`}>
                <SelectValue placeholder="Unassigned — pick class" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
                {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}
